import React, { useState, useEffect } from "react";
import { Button, Typography, Snackbar, Alert, Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";
import SlidingSidebar, {
  ButtonControls,
  SidebarAvatar,
  SidebarEditForm,
} from "../../../Components/SlidingSidebar";
import { useUser } from "../../../UserContext";

interface AddAccountProps {
  onSuccess?: () => void;
}

interface State {
  id: number;
  state: string;
  state_code: string;
}

interface City {
  city_id: number;
  state_code: string;
  state_name: string;
  city: string;
  county: string;
  county_id: number;
}

const AddAccount: React.FC<AddAccountProps> = ({ onSuccess }) => {
  console.log("AddAccount component rendered");
  const { selectedOwnerId } = useUser();
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [formData, setFormData] = useState({
    account: "",
    address: "",
    city: "",
    state_code: "",
    zip: "",
    avatar_url: "",
    county_id: null as number | null,
  });
  const [error, setError] = useState<string | null>(null);

  const handleEditChange = (field: string, value: any) => {
    if (field === "state_code") {
      setFormData({
        ...formData,
        state_code: value,
        city: "",
        county_id: null,
      });
      if (value) {
        fetchCitiesForState(value);
      }
    } else if (field === "city") {
      const selectedCity = cities.find((city) => city.city === value);
      setFormData({
        ...formData,
        city: value,
        county_id: selectedCity?.county_id || null,
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  const fetchCitiesForState = async (stateCode: string) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/accounts/cities/${stateCode}`
      );
      setCities(response.data);
    } catch (error) {
      console.error("Failed to fetch cities:", error);
    }
  };

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/accounts/states`
        );
        const statesData = response.data[0]?.data || [];
        setStates(statesData);
      } catch (error) {
        console.error("Failed to fetch states:", error);
      }
    };
    fetchStates();
  }, []);

  const handleOpen = () => {
    console.log("AddAccount handleOpen called");
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setFormData({
      account: "",
      address: "",
      city: "",
      state_code: "",
      zip: "",
      avatar_url: "",
      county_id: null,
    });
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/accounts/account`,
        {
          ...formData,
          owner_id: selectedOwnerId,
        }
      );

      if (response.status === 201) {
        handleClose();
        setSnackbarOpen(true);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      console.error("Error creating account:", error.response?.data || error);
      setError(error.response?.data?.error || "Failed to create account");
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleAvatarUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/accounts/account/upload-avatar`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      handleEditChange("avatar_url", response.data.avatar_url);
    } catch (error) {
      console.error("Error uploading avatar:", error);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<CloudUploadIcon />}
      >
        Add Account
      </Button>

      <SlidingSidebar open={open} onClose={handleClose} width="500px">
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
            <SidebarAvatar
              src={formData.avatar_url || ""}
              alt={formData.account || "Account Avatar"}
              onEditAvatar={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/*";
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    handleAvatarUpload(file);
                  }
                };
                input.click();
              }}
            />

            <SidebarEditForm
              sections={[
                {
                  title: "Account Information",
                  fields: [
                    {
                      label: "Account Name",
                      value: formData.account,
                      field: "account",
                      required: true,
                    },
                    {
                      label: "Address",
                      value: formData.address,
                      field: "address",
                      required: true,
                    },
                    {
                      label: "State",
                      value: formData.state_code,
                      field: "state_code",
                      type: "autocomplete",
                      options: states.map((state) => ({
                        label: `${state.state_code}`,
                        value: state.state_code,
                      })),
                      required: true,
                    },
                    {
                      label: "City",
                      value: formData.city,
                      field: "city",
                      type: "autocomplete",
                      options: cities.map((city) => ({
                        label: city.city,
                        value: city.city,
                      })),
                      disabled: !formData.state_code,
                      required: true,
                    },
                    {
                      label: "ZIP",
                      value: formData.zip,
                      field: "zip",
                      required: true,
                    },
                  ],
                },
              ]}
              onValueChange={handleEditChange}
            />
          </Box>

          <ButtonControls>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Create Account
            </Button>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
          </ButtonControls>
        </Box>
      </SlidingSidebar>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Account created successfully
        </Alert>
      </Snackbar>

      {error && (
        <Typography color="error" sx={{ mt: 2, px: 1 }}>
          {error}
        </Typography>
      )}
    </>
  );
};

export default AddAccount;
